// ---------------------------------------------------------------------------
// Estágio 2 — código + senha nova.
//
// Regras do 0098 §4 que esta tela precisa refletir:
//  - `code` é STRING de 6 dígitos ("042910" tem zero à esquerda significativo).
//  - Validade de 15 minutos → contador regressivo.
//  - 5 tentativas erradas QUEIMAM o desafio (nem o código certo passa depois).
//  - Pedir um novo código INVALIDA o anterior — o usuário costuma tentar o
//    primeiro e-mail que abriu, então isso é dito na tela, não só no erro.
// ---------------------------------------------------------------------------

import React from 'react';
import PropTypes from 'prop-types';

import FormField from '../../../../components/Form/FormField';
import { AuthForm } from '../../../../styles/auth/AuthLayout';
import { PrimaryButton } from '../../../../styles/ui/Button';
import { TextLink } from '../../../../styles/ui/Link';
import {
   StageGrid,
   StageSection,
   SectionHead,
   TargetChip,
   StageNote,
   FormFoot,
} from '../styled';

export default function CodeStage({
   email,
   form,
   fieldErrors,
   onFieldChange,
   onSubmit,
   onUseAnotherEmail,
   onResend,
   isSubmitting,
   countdownLabel,
   isExpired,
   resendCooldown,
}) {
   const canResend = resendCooldown === 0 && !isSubmitting;

   return (
      <AuthForm onSubmit={onSubmit} noValidate>
         <StageGrid>
            <StageSection aria-labelledby="stage-code-head">
               <SectionHead id="stage-code-head">
                  Código de verificação
               </SectionHead>

               <TargetChip>
                  <span>
                     Código enviado para <strong>{email}</strong>
                  </span>
                  <TextLink
                     as="button"
                     type="button"
                     onClick={onUseAnotherEmail}
                  >
                     Usar outro e-mail
                  </TextLink>
               </TargetChip>

               <FormField
                  label="Código de 6 dígitos"
                  name="code"
                  // `inputMode` numérico levanta o teclado certo no celular,
                  // mas o type continua text: `number` descartaria o zero à
                  // esquerda.
                  inputMode="numeric"
                  maxLength={6}
                  autoComplete="one-time-code"
                  placeholder="000000"
                  value={form.code}
                  onChange={onFieldChange('code')}
                  error={fieldErrors.code}
                  disabled={isSubmitting}
                  autoFocus
               />

               <StageNote $expired={isExpired}>
                  {isExpired
                     ? 'O código expirou. Peça um novo para continuar.'
                     : `O código expira em ${countdownLabel}.`}
               </StageNote>

               <StageNote>
                  Errou o código? Você pode pedir um novo — mas atenção: o
                  código anterior deixa de valer, e 5 tentativas erradas
                  invalidam o pedido.
               </StageNote>

               <div>
                  <TextLink
                     as="button"
                     type="button"
                     onClick={onResend}
                     disabled={!canResend}
                  >
                     {resendCooldown > 0
                        ? `Reenviar código (${resendCooldown}s)`
                        : 'Reenviar código'}
                  </TextLink>
               </div>
            </StageSection>

            <StageSection aria-labelledby="stage-password-head">
               <SectionHead id="stage-password-head">Nova senha</SectionHead>

               <FormField
                  label="Nova senha"
                  name="new_password"
                  type="password"
                  autoComplete="new-password"
                  value={form.new_password}
                  onChange={onFieldChange('new_password')}
                  error={fieldErrors.new_password}
                  hint="Entre 6 e 50 caracteres."
                  disabled={isSubmitting}
               />

               <FormField
                  label="Confirmar nova senha"
                  name="new_password_confirm"
                  type="password"
                  autoComplete="new-password"
                  value={form.new_password_confirm}
                  onChange={onFieldChange('new_password_confirm')}
                  error={fieldErrors.new_password_confirm}
                  disabled={isSubmitting}
               />

               <StageNote>
                  Ao redefinir, você é desconectado de todos os dispositivos e
                  precisa entrar com a nova senha.
               </StageNote>
            </StageSection>
         </StageGrid>

         <FormFoot>
            <TextLink to="/login">Voltar para o login</TextLink>

            {/* Grava a senha nova → azul (FC7 §G.1). */}
            <PrimaryButton type="submit" disabled={isSubmitting}>
               {isSubmitting ? 'Redefinindo...' : 'Redefinir senha'}
            </PrimaryButton>
         </FormFoot>
      </AuthForm>
   );
}

CodeStage.propTypes = {
   email: PropTypes.string.isRequired,
   form: PropTypes.shape({
      code: PropTypes.string,
      new_password: PropTypes.string,
      new_password_confirm: PropTypes.string,
   }).isRequired,
   fieldErrors: PropTypes.objectOf(PropTypes.string),
   onFieldChange: PropTypes.func.isRequired,
   onSubmit: PropTypes.func.isRequired,
   onUseAnotherEmail: PropTypes.func.isRequired,
   onResend: PropTypes.func.isRequired,
   isSubmitting: PropTypes.bool,
   countdownLabel: PropTypes.string,
   isExpired: PropTypes.bool,
   resendCooldown: PropTypes.number,
};

CodeStage.defaultProps = {
   fieldErrors: {},
   isSubmitting: false,
   countdownLabel: '',
   isExpired: false,
   resendCooldown: 0,
};
